import { useTranslation } from 'react-i18next';
import { CheckCircle2, Circle, CircleDot, XCircle, TimerOff, MinusCircle, Users } from 'lucide-react';
import type { ApprovalStage } from '@/api/hooks/useApprovalRequests';

const ICONS = {
  WAITING: Circle,
  ACTIVE: CircleDot,
  APPROVED: CheckCircle2,
  REJECTED: XCircle,
  SKIPPED: MinusCircle,
  EXPIRED: TimerOff,
} as const;

const COLOURS = {
  WAITING: 'text-muted-foreground',
  ACTIVE: 'text-amber-500',
  APPROVED: 'text-emerald-500',
  REJECTED: 'text-destructive',
  SKIPPED: 'text-muted-foreground',
  EXPIRED: 'text-destructive',
} as const;

function approverCount(stage: ApprovalStage): number | null {
  if (!stage.approverSpecJson) return null;
  try {
    const parsed = JSON.parse(stage.approverSpecJson) as { userIds?: number[] };
    return parsed.userIds?.length ?? 0;
  } catch {
    return null;
  }
}

/**
 * The chain of sign-offs: who is done, who is deciding now, who is still to
 * come.
 *
 * The zero-approver case is called out explicitly. A stage that resolved to
 * nobody is neither approved nor rejected by design — it stays stuck, and
 * without a visible marker that looks identical to "waiting patiently", which
 * is exactly the misconfiguration an admin needs to spot.
 */
export function ApprovalStageTimeline({ stages }: { stages: ApprovalStage[] }) {
  const { t } = useTranslation('approvals');

  return (
    <ol className="space-y-4">
      {stages.map((stage) => {
        const Icon = ICONS[stage.status] ?? Circle;
        const colour = COLOURS[stage.status] ?? 'text-muted-foreground';
        const count = approverCount(stage);
        const isStuck = stage.status === 'ACTIVE' && count === 0;

        return (
          <li key={stage.approvalRequestStageId} className="flex gap-3">
            <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${colour}`} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{stage.stageName}</span>
                <span className="text-xs text-muted-foreground">
                  {t(`stageStatus.${stage.status}`, { defaultValue: stage.status })}
                </span>
                {stage.escalationLevel > 0 && (
                  <span className="text-xs text-amber-600 dark:text-amber-400">
                    {t('detail.escalated', { level: stage.escalationLevel })}
                  </span>
                )}
              </div>

              <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {count === null
                    ? t('detail.approversUnresolved')
                    : t('detail.approverCount', { count })}
                </span>
                <span>
                  {t('detail.quorum')}: {stage.quorum}
                  {stage.quorum === 'N_OF_M' || stage.quorum === 'PERCENT'
                    ? ` (${stage.quorumThreshold})`
                    : ''}
                </span>
                {stage.dueDate && (
                  <span>{t('detail.due')}: {new Date(stage.dueDate).toLocaleString()}</span>
                )}
              </div>

              {isStuck && (
                <p className="mt-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-2 py-1 text-xs text-amber-700 dark:text-amber-300">
                  {t('detail.noEligibleApprovers')}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
